import { z } from 'zod';
/**
 * Documentos como o "HD" da pessoa: tudo o que o Secretário fez ou guardou para
 * ela, num lugar só, organizado por coisa da vida (não por pasta). Cada item
 * continua morando no seu módulo (entrega, receita, ata, lista, arquivo
 * guardado); aqui ficam só a lista unificada, a busca e as marcas que valem para
 * qualquer tipo: fixado, lixeira, coleção e de onde o item veio.
 */
export const DOCUMENT_KINDS = ['documento', 'receita', 'ata', 'lista', 'guardado', 'tela'];
export const documentKindSchema = z.enum(DOCUMENT_KINDS);
/** Nome de cada categoria como a pessoa vê (chips e seções). */
export const DOCUMENT_KIND_LABEL = {
    documento: 'Prontos',
    receita: 'Receitas',
    ata: 'Atas',
    lista: 'Listas',
    guardado: 'Guardados',
    tela: 'Materiais',
};
/** Dias que um item fica na lixeira antes de sair de vez. */
export const DOCUMENT_TRASH_DAYS = 30;
export const DOCUMENT_COLLECTION_MAX = 40;
export const DOCUMENT_QUERY_MAX = 80;
/** Itens por ação em lote (selecionar vários na tela). */
export const DOCUMENT_BULK_MAX = 200;
/** Candidatos que a procura por voz devolve: nunca uma lista longa falada. */
export const DOCUMENT_VOICE_CANDIDATES = 3;
/** Tamanho máximo de um arquivo guardado (fotos e PDFs). */
export const SAVED_FILE_MAX_BYTES = 20 * 1024 * 1024;
export const SAVED_FILE_TITLE_MAX = 120;
/** Tipos aceitos nos Guardados: fotos e PDF (o que se recebe e se manda no dia a dia). */
export const SAVED_FILE_MIMES = [
    'image/jpeg',
    'image/png',
    'image/webp',
    'image/heic',
    'image/heif',
    'application/pdf',
    // Apresentação montada na tela grande e guardada em PowerPoint.
    'application/vnd.openxmlformats-officedocument.presentationml.presentation',
];
export const PPTX_MIME = 'application/vnd.openxmlformats-officedocument.presentationml.presentation';
export const savedFileMimeSchema = z.enum(SAVED_FILE_MIMES);
const itemId = z.string().trim().min(1).max(64);
const collection = z.string().trim().min(1).max(DOCUMENT_COLLECTION_MAX);
/** De onde o item veio, em uma linha ("Da nossa conversa de 03/10"), e o link para lá. */
export const documentOriginSchema = z.object({
    text: z.string(),
    href: z.string().nullable(),
});
export const documentItemSchema = z.object({
    kind: documentKindSchema,
    id: itemId,
    title: z.string(),
    /** Linha curta de apoio (tipo do documento, rendimento, decisão, itens que faltam). */
    subtitle: z.string(),
    /** Página do item no app. */
    href: z.string(),
    /** Desenho do ícone: o tipo da receita, o serviço do documento, `ata`, `lista`, `foto` ou `pdf`. */
    glyph: z.string(),
    createdAt: z.iso.datetime({ offset: true }),
    updatedAt: z.iso.datetime({ offset: true }),
    pinnedAt: z.iso.datetime({ offset: true }).nullable(),
    trashedAt: z.iso.datetime({ offset: true }).nullable(),
    /** Quando sai de vez da lixeira (só para itens na lixeira). */
    purgeAt: z.iso.datetime({ offset: true }).nullable(),
    collection: z.string().nullable(),
    origin: documentOriginSchema,
    /** Só para os guardados. */
    sizeBytes: z.number().int().nonnegative().nullable(),
    /** Trecho onde a busca achou o termo, quando não foi no título. */
    match: z.string().nullable(),
});
export const documentStorageSchema = z.object({
    usedBytes: z.number().int().nonnegative(),
    limitBytes: z.number().int().nonnegative(),
    /** Passou do espaço (por exemplo, depois de mudar de plano): pode ler e baixar, não guardar mais. */
    over: z.boolean(),
    /** Busca dentro de fotos e PDFs e versões das receitas. */
    extras: z.boolean(),
});
export const documentListSchema = z.object({
    items: z.array(documentItemSchema),
    counts: z.record(documentKindSchema, z.number().int().nonnegative()),
    collections: z.array(z.object({ name: z.string(), count: z.number().int().nonnegative() })),
    trashCount: z.number().int().nonnegative(),
    storage: documentStorageSchema,
});
/** Filtros da lista (todos opcionais): busca, categoria, coleção e período. */
export const documentQuerySchema = z.object({
    q: z.string().trim().max(DOCUMENT_QUERY_MAX).optional(),
    kind: documentKindSchema.optional(),
    collection: collection.optional(),
    /** Período em dias corridos até hoje (30, 90, 365). */
    days: z.coerce.number().int().min(1).max(3650).optional(),
});
export const documentRefSchema = z.object({ kind: documentKindSchema, id: itemId });
/** Marcar um item: fixar ou desafixar, pôr numa coleção ou tirar (null), renomear um guardado. */
export const documentPatchSchema = z
    .object({
    pinned: z.boolean().optional(),
    collection: collection.nullable().optional(),
    title: z.string().trim().min(1).max(SAVED_FILE_TITLE_MAX).optional(),
})
    .refine((value) => Object.keys(value).length > 0, 'Nada para mudar.');
export const DOCUMENT_BULK_ACTIONS = ['trash', 'restore', 'purge', 'pin', 'unpin'];
export const documentBulkSchema = z.object({
    action: z.enum(DOCUMENT_BULK_ACTIONS),
    items: z.array(documentRefSchema).min(1).max(DOCUMENT_BULK_MAX),
});
export const documentBulkResultSchema = z.object({ done: z.number().int().nonnegative() });
/** Metadados de um arquivo guardado (o conteúdo vem à parte, em binário). */
export const savedFileSchema = z.object({
    id: z.uuid(),
    title: z.string(),
    fileName: z.string(),
    mime: savedFileMimeSchema,
    sizeBytes: z.number().int().nonnegative(),
    createdAt: z.iso.datetime({ offset: true }),
    origin: documentOriginSchema,
    /** Já dá para achar pelo que está escrito dentro (planos com a busca dentro). */
    searchable: z.boolean(),
});
/**
 * O que o app manda junto do arquivo (no cabeçalho `x-doc-meta`, JSON em base64url):
 * o nome e, quando veio de uma conversa, quem mandou e a conversa.
 */
export const savedFileMetaSchema = z.object({
    name: z.string().trim().min(1).max(200),
    title: z.string().trim().min(1).max(SAVED_FILE_TITLE_MAX).optional(),
    from: z.string().trim().max(80).optional(),
    roomId: z.string().trim().max(255).optional(),
    /** Quando a mensagem chegou (ISO); sem isso, agora. */
    at: z.iso.datetime({ offset: true }).optional(),
});
/** Versão anterior de uma receita (antes de uma edição). */
export const recipeVersionSchema = z.object({
    id: z.uuid(),
    title: z.string(),
    createdAt: z.iso.datetime({ offset: true }),
});
export const recipeVersionListSchema = z.object({ versions: z.array(recipeVersionSchema) });
/** Busca sem acento e sem diferença de maiúscula ("Fubá" acha "fuba"). */
export function foldText(value) {
    return value.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().replace(/\s+/g, ' ').trim();
}
/** Tamanho legível ("1,2 MB", "340 KB", "5 GB"). */
export function formatBytes(bytes) {
    const unit = (value, name) => `${Number.isInteger(value) || value >= 10 ? Math.round(value) : value.toFixed(1).replace('.', ',')} ${name}`;
    if (bytes >= 1024 * 1024 * 1024)
        return unit(bytes / (1024 * 1024 * 1024), 'GB');
    if (bytes >= 1024 * 1024)
        return unit(bytes / (1024 * 1024), 'MB');
    return `${Math.max(1, Math.round(bytes / 1024))} KB`;
}
